import { IShowroomStateFilter } from '@pages/showroom-v2/utils/models/IShowroomStateFilter';
import { createSlice, current, PayloadAction } from '@reduxjs/toolkit';
import { LocalStorage } from '@service/LocalStorage';

import { FlatNumberEnum } from './AllHouseFilter';

interface IShowroomState {
  filter: IShowroomStateFilter;
  drawer: {
    id: number | null;
    floor: number | null;
    visible: boolean;
  };
  listSettingDrawer: boolean;
  apartments: any[];
  apartmentActiveIndex: number;
  petition: {
    visible?: boolean;
    houseName?: string;
  };
  flatNumber: keyof typeof FlatNumberEnum;
  flatCount: number;
  showroomV2: boolean;
  switchFilterStatus: boolean;
  isViewBookingBadge: boolean;
  activeInfoTab: 'flat' | 'booking';
}

const initialState: IShowroomState = {
  filter: {
    page: 1,
    size: 10,
    orders: {}
  },
  drawer: {
    id: null,
    floor: null,
    visible: false
  },
  listSettingDrawer: false,
  apartments: [{}],
  apartmentActiveIndex: 0,
  petition: {
    visible: false,
    houseName: ''
  },
  flatNumber: (LocalStorage.get('flatNumber') as FlatNumberEnum) || 'number',
  flatCount: 0,
  showroomV2: (LocalStorage.get<boolean>('showroomV2') as boolean) || false,
  switchFilterStatus: false,
  activeInfoTab: 'flat',
  isViewBookingBadge: JSON.parse(LocalStorage.get('isViewBookingBadge') || 'false')
};

export const ShowroomSlice = createSlice({
  name: 'showroom-slice',
  initialState,
  reducers: {
    setShowroomFilter: (state, action: PayloadAction<Partial<IShowroomStateFilter>>) => {
      const { filter } = current(state);

      state.filter = { ...filter, ...action.payload };
    },
    setShowroomNewFilter: (state, action: PayloadAction<Partial<IShowroomStateFilter>>) => {
      const { filter } = current(state);

      state.filter = {
        houseId: filter?.houseId,
        buildingIds: filter?.buildingIds,
        orders: filter?.orders,
        ...action.payload
      };
    },
    setShowroomDrawer: (
      state,
      action: PayloadAction<{
        id: number | null;
        floor: number | null;
        visible: boolean;
      }>
    ) => {
      state.drawer = { ...action.payload };
    },
    setListSettingDrawer: (state, action: PayloadAction<boolean>) => {
      state.listSettingDrawer = action.payload;
    },
    setShowroomApartments: (state, action: any) => {
      state.apartments = action.payload;
    },
    setShowroomApartmentActive: (state, action: any) => {
      state.apartmentActiveIndex = action.payload;
    },
    setResetActiveFlat: state => {
      state.apartmentActiveIndex = 0;
      state.apartments = [{}];
      state.drawer = {
        floor: null,
        id: null,
        visible: false
      };
    },
    setShowroomPetition: (state, action: PayloadAction<{ visible?: boolean; houseName?: string }>) => {
      state.petition = action.payload;
    },
    setShowroomFlatNumber: (state, action: { payload: keyof typeof FlatNumberEnum }) => {
      state.flatNumber = action?.payload;
      LocalStorage.set('flatNumber', action?.payload);
    },
    setShowroomFlatCount: (state, action: PayloadAction<number>) => {
      state.flatCount = action?.payload;
    },
    setShowroomV2: (state, action: PayloadAction<boolean>) => {
      state.showroomV2 = action.payload;
      LocalStorage.set('showroomV2', String(action.payload));
    },
    setSwitchFilterStatus: (state, action: PayloadAction<boolean>) => {
      state.switchFilterStatus = action.payload;
    },
    setIsViewBookingBadge: (state, action: PayloadAction<boolean>) => {
      state.isViewBookingBadge = action.payload;
      LocalStorage.set('isViewBookingBadge', String(action.payload));

      if (!action.payload) {
        state.activeInfoTab = 'flat';
      }
    },
    setActiveInfoTab: (state, action: PayloadAction<'flat' | 'booking'>) => {
      state.activeInfoTab = action.payload;
    }
  }
});

export const {
  setShowroomFilter,
  setShowroomDrawer,
  setListSettingDrawer,
  setShowroomNewFilter,
  setShowroomApartments,
  setShowroomApartmentActive,
  setShowroomPetition,
  setShowroomFlatNumber,
  setShowroomFlatCount,
  setResetActiveFlat,
  setShowroomV2,
  setSwitchFilterStatus,
  setIsViewBookingBadge,
  setActiveInfoTab
} = ShowroomSlice.actions;
export default ShowroomSlice.reducer;
